/**
 * StreamXNavTicker — StreamX Sprint 3d + 3e.
 * ------------------------------------------------------------------
 * Ambient broadcast ticker for the cinematic StreamX nav.
 *
 * Two modes, one cohesive surface:
 *
 *   MODE A — Normal rotation (Sprint 3d)
 *     • Reads `pulse.trending.top_live` (up to 3 currently-live streams)
 *     • Auto-rotates every 6 seconds with fade + upward 8px motion
 *     • Each slide clickable → /streamx/watch/:id
 *     • LIVE pulse dot (red) · academy_name · title · viewer count
 *     • Soft red bloom backdrop + glass border
 *     • Desktop: pip indicators · Mobile: compact + no pips
 *
 *   MODE B — "Just Went Live" newsroom alert (Sprint 3e)
 *     • When `pulse.trending.just_went_live` arrives (a stream that started
 *       within the last 90s) AND we haven't shown it yet, INTERRUPT the
 *       normal rotation for exactly one cycle (~6s) with a green-tinted
 *       newsroom slide
 *     • Green pulse dot (#10b981) · "JUST WENT LIVE" label · brighter glow
 *     • Subtle CTA microcopy: "Join the broadcast →"
 *     • After 1 cycle, the alert is marked dismissed (so it never
 *       re-shows) and normal rotation resumes — the stream itself stays
 *       in the regular `top_live` carousel
 *     • Re-activation only happens for a NEW stream_id (next launch)
 *
 *   Design rules (verbatim from the user's spec):
 *     ✅ subtle motion · glow bloom · slight vertical slide (350-500ms)
 *     ❌ NO sound · NO modals · NO popups · NO hard flashes
 *
 * Performance:
 *   • Reuses the existing 60s pulse poll — zero extra network calls
 *   • Local state machine for alert lifecycle (no extra timers when idle)
 */
import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Radio, ArrowRight } from 'lucide-react';

const ROTATION_MS = 6000;
const ALERT_CYCLE_MS = 6500; // One cycle + tiny buffer so the green tint
                              // finishes blooming before we hand off to
                              // the red rotation.
const ENTER_DURATION = 0.42;  // seconds — calm broadcast fade
const ALERT_ENTER_DURATION = 0.5; // slightly longer entrance for prestige
const GREEN = '#10b981';
const GREEN_SOFT = '#6ee7b7';

function fmtViews(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
}

// ── Red LIVE heartbeat dot — broadcast-grade ─────────────────────────

function LivePulseDot() {
  return (
    <span className="relative inline-flex h-2 w-2 flex-shrink-0" aria-hidden>
      <motion.span
        className="absolute inset-[-4px] rounded-full"
        animate={{ opacity: [0, 0.55, 0] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          background: 'radial-gradient(circle, rgba(255,59,48,0.7), rgba(255,59,48,0) 70%)',
          filter: 'blur(3px)',
        }}
      />
      <motion.span
        className="absolute inline-flex h-2 w-2 rounded-full"
        animate={{ opacity: [0.75, 1, 0.75], scale: [1, 1.06, 1] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
        style={{ background: '#FF3B30' }}
      />
      <span
        className="relative inline-flex h-2 w-2 rounded-full"
        style={{ background: '#FF3B30', boxShadow: '0 0 8px rgba(255,59,48,0.85)' }}
      />
    </span>
  );
}

// ── Green "Just went live" heartbeat dot — newsroom freshness ────────

function FreshPulseDot() {
  return (
    <span className="relative inline-flex h-2 w-2 flex-shrink-0" aria-hidden>
      <motion.span
        className="absolute inset-[-5px] rounded-full"
        animate={{ opacity: [0, 0.7, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          background: `radial-gradient(circle, ${GREEN}b3, ${GREEN}00 70%)`,
          filter: 'blur(4px)',
        }}
      />
      <motion.span
        className="absolute inline-flex h-2 w-2 rounded-full"
        animate={{ opacity: [0.8, 1, 0.8], scale: [1, 1.08, 1] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        style={{ background: GREEN }}
      />
      <span
        className="relative inline-flex h-2 w-2 rounded-full"
        style={{ background: GREEN, boxShadow: `0 0 10px ${GREEN}ee` }}
      />
    </span>
  );
}

// ── Slides ───────────────────────────────────────────────────────────

function LiveSlide({ item }) {
  return (
    <Link
      to={`/streamx/watch/${item.stream_id}`}
      data-testid={`streamx-nav-ticker-slide-${item.stream_id}`}
      title="Watch live now"
      className="group inline-flex items-center gap-2 sm:gap-3 px-1 max-w-full"
    >
      <LivePulseDot />
      <span
        className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] flex-shrink-0"
        style={{ color: '#FF6B5C' }}
      >
        Live now
      </span>
      <span className="text-white/25 flex-shrink-0" aria-hidden>·</span>
      <span className="text-[12px] sm:text-[13px] font-semibold text-white tracking-tight truncate min-w-0 group-hover:text-[#FF6B5C] transition-colors">
        {item.academy_name}
      </span>
      {item.title && item.title !== 'Live broadcast' && (
        <>
          <span className="hidden sm:inline text-white/25 flex-shrink-0" aria-hidden>·</span>
          <span className="hidden sm:inline text-[12px] font-medium text-white/65 truncate min-w-0">
            {item.title}
          </span>
        </>
      )}
      <span className="text-white/25 flex-shrink-0" aria-hidden>·</span>
      <span className="text-[12px] font-semibold text-white/85 inline-flex items-center gap-1 tabular-nums flex-shrink-0">
        <Eye className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
        <span className="hidden sm:inline">{fmtViews(item.viewer_count)} watching</span>
        <span className="sm:hidden">{fmtViews(item.viewer_count)}</span>
      </span>
    </Link>
  );
}

function FreshSlide({ item }) {
  return (
    <Link
      to={`/streamx/watch/${item.stream_id}`}
      data-testid={`streamx-nav-ticker-fresh-${item.stream_id}`}
      title="Join the broadcast"
      className="group inline-flex items-center gap-2 sm:gap-3 px-1 max-w-full"
    >
      <FreshPulseDot />
      <span
        className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] flex-shrink-0"
        style={{ color: GREEN_SOFT }}
      >
        Just went live
      </span>
      <span className="text-white/25 flex-shrink-0" aria-hidden>·</span>
      <span
        className="text-[12px] sm:text-[13px] font-semibold tracking-tight truncate min-w-0 transition-colors"
        style={{ color: 'white' }}
      >
        {item.academy_name}
      </span>
      {item.title && item.title !== 'Live broadcast' && (
        <>
          <span className="hidden sm:inline text-white/25 flex-shrink-0" aria-hidden>·</span>
          <span className="hidden sm:inline text-[12px] font-medium text-white/75 truncate min-w-0">
            {item.title}
          </span>
        </>
      )}
      <span className="hidden sm:inline text-white/25 flex-shrink-0" aria-hidden>·</span>
      <span
        className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold tracking-tight transition-transform group-hover:translate-x-0.5"
        style={{ color: GREEN_SOFT }}
      >
        Join the broadcast <ArrowRight className="h-3 w-3" />
      </span>
    </Link>
  );
}

// ── Ticker shell ─────────────────────────────────────────────────────

export default function StreamXNavTicker({ pulse }) {
  const items = pulse?.trending?.top_live || [];
  const fresh = pulse?.trending?.just_went_live;

  // Rotation index for the normal carousel.
  const [idx, setIdx] = useState(0);

  // Alert lifecycle: which fresh stream_id is currently being shown.
  // null when nothing is being announced.
  const [alertActiveId, setAlertActiveId] = useState(null);

  // Tracks all fresh stream IDs we have already announced.  Persistent
  // across pulse re-fetches so the same launch never re-fires.
  const dismissedRef = useRef(new Set());

  // ── Detect a fresh stream and inject the newsroom alert ──────────
  useEffect(() => {
    if (!fresh || !fresh.stream_id) return;
    if (typeof fresh.seconds_since_started === 'number' && fresh.seconds_since_started > 90) return;
    if (dismissedRef.current.has(fresh.stream_id)) return;
    if (alertActiveId === fresh.stream_id) return;

    setAlertActiveId(fresh.stream_id);
    const t = setTimeout(() => {
      dismissedRef.current.add(fresh.stream_id);
      setAlertActiveId(null);
    }, ALERT_CYCLE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh?.stream_id, fresh?.seconds_since_started]);

  // Keep rotation index valid if the upstream list shrinks
  useEffect(() => {
    if (idx >= items.length && items.length > 0) setIdx(0);
  }, [items.length, idx]);

  // 6s rotation — only when there are 2+ entries.  Pauses while an
  // alert is active (the timer keeps firing but the active alert takes
  // visual priority).
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % items.length);
    }, ROTATION_MS);
    return () => clearInterval(t);
  }, [items.length]);

  const showingAlert = !!alertActiveId && fresh && fresh.stream_id === alertActiveId;
  const currentLive = items[idx] || items[0];

  // Hidden when there is nothing to say at all.
  if (!showingAlert && items.length === 0) return null;

  return (
    <div
      data-testid="streamx-nav-ticker"
      data-alert-mode={showingAlert ? 'fresh' : 'live'}
      className="w-full overflow-hidden border-b transition-colors duration-700"
      style={{
        background: showingAlert
          ? `linear-gradient(90deg, ${GREEN}1c 0%, ${GREEN}22 25%, ${GREEN}1c 50%, ${GREEN}0a 100%), rgba(0,0,0,0.65)`
          : 'linear-gradient(90deg, rgba(255,59,48,0.06) 0%, rgba(255,59,48,0.10) 20%, rgba(255,59,48,0.06) 50%, rgba(255,59,48,0.02) 100%), rgba(0,0,0,0.65)',
        borderColor: showingAlert ? `${GREEN}55` : 'rgba(255,59,48,0.18)',
        backdropFilter: 'blur(24px)',
        boxShadow: showingAlert
          ? `inset 0 0 32px ${GREEN}33, 0 0 18px ${GREEN}26`
          : 'inset 0 0 24px rgba(255,59,48,0.08)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-9 sm:h-10 flex items-center justify-center sm:justify-between gap-3 relative">
        {/* Left flag — switches identity in alert mode */}
        <div
          className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.22em] text-white flex-shrink-0 transition-all duration-700"
          style={
            showingAlert
              ? {
                  background: `linear-gradient(135deg, ${GREEN}66, ${GREEN}33)`,
                  border: `1px solid ${GREEN}99`,
                  boxShadow: `0 0 12px ${GREEN}55`,
                }
              : {
                  background: 'linear-gradient(135deg, rgba(255,59,48,0.28), rgba(153,0,0,0.28))',
                  border: '1px solid rgba(255,59,48,0.45)',
                }
          }
        >
          <Radio className="h-2.5 w-2.5" />
          {showingAlert ? 'Newsroom' : 'Network'}
        </div>

        {/* Rotating slide — alert OR live */}
        <div className="flex-1 min-w-0 flex items-center justify-center overflow-hidden">
          <AnimatePresence mode="wait">
            {showingAlert ? (
              <motion.div
                key={`fresh-${fresh.stream_id}`}
                initial={{ opacity: 0, y: 10, filter: 'blur(4px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -10, filter: 'blur(2px)' }}
                transition={{ duration: ALERT_ENTER_DURATION, ease: [0.22, 1, 0.36, 1] }}
                className="w-full flex justify-center"
              >
                <FreshSlide item={fresh} />
              </motion.div>
            ) : (
              <motion.div
                key={`live-${currentLive?.stream_id || 'none'}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: ENTER_DURATION, ease: [0.22, 1, 0.36, 1] }}
                className="w-full flex justify-center"
              >
                {currentLive && <LiveSlide item={currentLive} />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Pips — desktop only, hidden during alert (alert is one-shot) */}
        {!showingAlert && items.length > 1 && (
          <div className="hidden sm:inline-flex items-center gap-1.5 flex-shrink-0" data-testid="streamx-nav-ticker-pips">
            {items.map((it, i) => (
              <button
                key={it.stream_id}
                onClick={() => setIdx(i)}
                aria-label={`Show ${it.academy_name}`}
                data-testid={`streamx-nav-ticker-pip-${i}`}
                className="h-1 rounded-full transition-all duration-500"
                style={{
                  width: idx === i ? 16 : 6,
                  background: idx === i ? '#FF3B30' : 'rgba(255,255,255,0.25)',
                  boxShadow: idx === i ? '0 0 6px rgba(255,59,48,0.65)' : 'none',
                }}
              />
            ))}
          </div>
        )}

        {/* Alert-mode spacer (preserves right-side balance when pips hide) */}
        {showingAlert && <div className="hidden sm:block w-14 flex-shrink-0" aria-hidden />}
      </div>
    </div>
  );
}
